import targets from './salesDataTargets.json';
import { MONTHS } from './constants';

export const sum = (values) => values.reduce((a, b) => a + b, 0);

// Cancelled invoices are listed but do not count towards turnover
export const countedOnly = (invoices) => invoices.filter((i) => !i.cancelled);

export const toSek = (invoice, amount) => {
    if (!Number.isFinite(invoice.salesExchangeRate) || invoice.salesExchangeRate <= 0) {
        throw new Error(`Invalid sales exchange rate for invoice ${invoice.documentNumber}`);
    }
    return (amount || 0) * invoice.salesExchangeRate;
};

// The amount that counts towards turnover in SEK: net plus freight, excluding VAT
export const getSalesAmount = (invoice) => toSek(invoice, (invoice.net || 0) + (invoice.freight || 0));

export const canTrack = (invoice) => Boolean(invoice.orderReference && invoice.deliveryZipCode);

// Articles with these numbers are generic: the same number is used for different products, told apart by the row description
const GENERIC_ARTICLE_PREFIX = '59999';

// The web shop order numbers (LS12345) are kept on the Fortnox invoices as order number and reference
const ORDER_NUMBER_PATTERN = /\bLS\d+\b/gi;

const findOrderNumbers = (invoice) => [invoice.yourOrderNumber, invoice.yourReference]
    .flatMap((value) => (value || '').match(ORDER_NUMBER_PATTERN) || [])
    .map((number) => number.toUpperCase());

// The order numbers of an invoice. A credit invoice without an order number of its own gets the ones of the invoice that it credits.
// invoicesByNumber: Map of document number to invoice, used to find the credited invoice.
export const getInvoiceOrderNumbers = (invoice, invoicesByNumber) => {
    const own = findOrderNumbers(invoice);
    if (own.length > 0 || !invoice.invoiceReference) return own;

    const credited = invoicesByNumber?.get(invoice.invoiceReference);
    return credited ? findOrderNumbers(credited) : [];
};

// The distinct order numbers of the invoices, the newest invoice first
export const getOrderNumbers = (invoices, invoicesByNumber) => {
    const numbers = new Set();
    [...invoices]
        .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || ''))
        .forEach((invoice) => getInvoiceOrderNumbers(invoice, invoicesByNumber).forEach((number) => numbers.add(number)));

    return [...numbers];
};

// The Fortnox orders of the invoices, the newest invoice first. The invoices have no customer e-mail address, only the orders have.
// A credit invoice gets both its own order and the order of the invoice that it credits.
export const getFortnoxOrderNumbers = (invoices, invoicesByNumber) => {
    const numbers = new Set();
    [...invoices]
        .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || ''))
        .forEach((invoice) => [invoice, invoicesByNumber?.get(invoice.invoiceReference)]
            .forEach((source) => { if (source?.orderReference > 0) numbers.add(source.orderReference); }));

    return [...numbers];
};

export const getArticleKey = (row) => (row.articleNumber.startsWith(GENERIC_ARTICLE_PREFIX)
    ? `${row.articleNumber}|${(row.description || '').trim().toLowerCase()}`
    : row.articleNumber);

// One entry per article with the summed quantity, the value (price x quantity before any row discount) and the invoices it is on.
// absolute counts the quantities of credit invoice rows (negative) as positive.
export const aggregateArticles = (invoices, { absolute = false } = {}) => {
    const byArticle = new Map();
    invoices.forEach((invoice) => {
        (invoice.rows || []).filter((row) => row.articleNumber).forEach((row) => {
            const quantity = absolute ? Math.abs(row.deliveredQuantity || 0) : row.deliveredQuantity || 0;
            const key = getArticleKey(row);
            const entry = byArticle.get(key) || { key, articleNumber: row.articleNumber, description: row.description, quantity: 0, value: 0, invoices: [] };
            entry.quantity += quantity;
            entry.value += toSek(invoice, (row.price || 0) * quantity);
            if (!entry.invoices.includes(invoice)) entry.invoices.push(invoice);
            byArticle.set(key, entry);
        });
    });

    return [...byArticle.values()];
};

export const getTrackingUrl = (invoice) =>
    `https://tracking.postnox.se/bft?order=${encodeURIComponent(invoice.orderReference)}&zip=${encodeURIComponent(invoice.deliveryZipCode.replace(/\s/g, ''))}`;

// Index of the latest month that has started in the target year, -1 if none
export const getLastStartedMonth = (now = new Date()) => {
    if (now.getFullYear() > targets.year) return 11;
    return now.getFullYear() === targets.year ? now.getMonth() : -1;
};

// Splits an inclusive ISO date range into consecutive windows of at most `days` days
export const buildDateWindows = (fromDate, toDate, days) => {
    const windows = [];
    const end = new Date(`${toDate}T00:00:00Z`);
    const toIso = (date) => date.toISOString().substring(0, 10);

    for (let start = new Date(`${fromDate}T00:00:00Z`); start <= end;) {
        const windowEnd = new Date(start);
        windowEnd.setUTCDate(windowEnd.getUTCDate() + days - 1);
        const last = windowEnd < end ? windowEnd : end;
        windows.push({ from: toIso(start), to: toIso(last) });

        start = new Date(last);
        start.setUTCDate(start.getUTCDate() + 1);
    }

    return windows;
};

export const getMonthIndex = (invoice) => {
    const date = invoice.invoiceDate;
    if (!date) return -1;
    const parsed = new Date(date);
    return parsed.getFullYear() === targets.year ? parsed.getMonth() : -1;
};

const matchesPerson = (invoice, person) => {
    const reference = (invoice.ourReference || '').toLowerCase();
    return reference.includes(person.name.toLowerCase());
};

// buckets[personIndex][month] = invoices counted in that cell
export const buildBuckets = (invoices) => {
    const buckets = targets.salespersons.map(() => Array.from({ length: 12 }, () => []));

    invoices.forEach((invoice) => {
        const month = getMonthIndex(invoice);
        if (month < 0) return;

        const personIndex = invoice.isWeb
            ? targets.salespersons.findIndex((p) => p.isWeb)
            : targets.salespersons.findIndex((p) => !p.isWeb && !p.isDefault && matchesPerson(invoice, p));

        // Non-web invoices not matching a named salesperson belong to the default one (Martin)
        const resolvedIndex = personIndex >= 0 || invoice.isWeb
            ? personIndex
            : targets.salespersons.findIndex((p) => p.isDefault);

        if (resolvedIndex >= 0) {
            buckets[resolvedIndex][month].push(invoice);
        }
    });

    return buckets;
};

// Invoices that were loaded but did not end up in any bucket (no invoice date in the target year, or no salesperson)
export const getUnplacedInvoices = (invoices, buckets) => {
    const placed = new Set(buckets.flatMap((months) => months.flat()));
    return invoices.filter((invoice) => !placed.has(invoice));
};

// results[personIndex][month] = summed amount (net + freight) of non-cancelled invoices
export const buildResults = (buckets) =>
    buckets.map((months) => months.map((list) => sum(countedOnly(list).map(getSalesAmount))));

// Invoices whose customer name or invoice number contains the query, newest first
export const searchInvoices = (invoices, query) => {
    const term = query.trim().toLowerCase();
    return invoices
        .filter((invoice) =>
            (invoice.customerName || '').toLowerCase().includes(term)
            || String(invoice.documentNumber ?? '').includes(term))
        .sort((a, b) =>
            (b.invoiceDate || '').localeCompare(a.invoiceDate || '')
            || (b.documentNumber ?? 0) - (a.documentNumber ?? 0));
};

const TEXT_FIELDS = [
    'documentNumber', 'invoiceDate', 'orderReference', 'customerNumber', 'customerName', 'ourReference', 'yourReference',
    'yourOrderNumber', 'wayOfDelivery', 'deliveryName', 'deliveryAddress1', 'deliveryZipCode', 'deliveryCity', 'currency',
];
const AMOUNT_FIELDS = ['net', 'freight', 'totalVAT', 'total'];

// Amounts in the forms a user may type them, without spaces: 1234.5, 1234.50 and 1234,50
const amountForms = (value) => {
    if (value === null || value === undefined) return [];
    const fixed = value.toFixed(2);
    return [String(value), fixed, fixed.replace('.', ',')];
};

// Invoices where any text field or amount field contains the query (case-insensitive). Keeps the order of the list.
export const filterInvoices = (invoices, query) => {
    const term = query.trim().toLowerCase();
    if (!term) return invoices;

    const amountTerm = term.replace(/\s/g, '');
    return invoices.filter((invoice) =>
        TEXT_FIELDS.some((field) => String(invoice[field] ?? '').toLowerCase().includes(term))
        || AMOUNT_FIELDS.some((field) => amountForms(invoice[field]).some((form) => form.includes(amountTerm))));
};

// personIndex / month are null when the selection covers all persons / all months
export const getDrillInvoices = (buckets, { personIndex, month }) => {
    const personIndexes = personIndex === null ? targets.salespersons.map((_, i) => i) : [personIndex];
    const monthIndexes = month === null ? MONTHS.map((_, m) => m) : [month];
    return personIndexes
        .flatMap((i) => monthIndexes.flatMap((m) => buckets[i][m]))
        .sort((a, b) => (a.invoiceDate || '').localeCompare(b.invoiceDate || ''));
};
